import React, { useCallback } from 'react';
import {
    Drawer,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    Typography,
    Toolbar,
} from '@mui/material';
import { useLocation, useNavigate } from 'react-router-dom';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import HomeIcon from '@mui/icons-material/Home';
import HistoryIcon from '@mui/icons-material/History';
import BusinessIcon from '@mui/icons-material/Business';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import CorporateFareIcon from '@mui/icons-material/CorporateFare';
import PersonSearchIcon from '@mui/icons-material/PersonSearch';
import HomeWorkIcon from '@mui/icons-material/HomeWork';
import VerifiedUserIcon from '@mui/icons-material/VerifiedUser';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import FaceIcon from '@mui/icons-material/Face';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import BadgeIcon from '@mui/icons-material/Badge';
import PeopleIcon from '@mui/icons-material/People';
import FingerprintIcon from '@mui/icons-material/Fingerprint';
import PhoneIcon from '@mui/icons-material/Phone';
import { rolePermissions, getUserRolesFromToken } from '../../config/routePermissions';

const NAV_ITEMS = [
    { label: 'Home', icon: <HomeIcon />, path: '/' },
    { label: 'Business Online Enquiry', icon: <BusinessIcon />, path: '/verification/business-online-enquiry' },
    // { label: 'Indigent Search', icon: <SearchIcon />, path: '/search' },
    { label: 'Account Verification Service', icon: <AccountBalanceIcon />, path: '/verification/account-verification-service' },
    { label: 'CIPC Report', icon: <CorporateFareIcon />, path: '/verification/cipc-report' },
    { label: 'Consumer Trace', icon: <PersonSearchIcon />, path: '/verification/consumer-trace' },
    { label: 'Deeds Enquiry', icon: <HomeWorkIcon />, path: '/verification/deeds-enquiry' },
    { label: 'DO Verification System', icon: <VerifiedUserIcon />, path: '/verification/digital-onboarding-verification-system' },
    { label: 'Director Enquiry Report', icon: <SupervisorAccountIcon />, path: '/verification/director-enquiry-report' },
    { label: 'Faceview Plus', icon: <FaceIcon />, path: '/verification/faceview-plus' },
    { label: 'KYC Online', icon: <FactCheckIcon />, path: '/verification/kyc-online' },
    { label: 'Realtime ID Verification', icon: <BadgeIcon />, path: '/verification/realtime-id-verification' },
    { label: 'Realtime Marriage Verification', icon: <PeopleIcon />, path: '/verification/realtime-marriage-verification' },
    { label: 'Realtime IDV', icon: <FingerprintIcon />, path: '/verification/realtime-idv' },
    { label: 'Telephone Trace', icon: <PhoneIcon />, path: '/verification/telephone-trace' },
    { label: 'History', icon: <HistoryIcon />, path: '/verification/history' },
];


const listItemStyles = {
    borderRadius: '12px',
    '&:hover': {
        backgroundColor: '#EEF0FF',
    },
    paddingY: '8px',
    paddingX: '12px',
};

const activeListItemStyles = {
    ...listItemStyles,
    backgroundColor: '#EEF0FF',
    color: '#0A1D56',
};

interface VerificationSystemSidebarProps {
    isMobile: boolean;
    isOpen: boolean;
    onClose: () => void;
}

const VerificationSystemSidebar: React.FC<VerificationSystemSidebarProps> = ({ isMobile, isOpen, onClose }) => {
    const location = useLocation();
    const navigate = useNavigate();
    const DRAWER_WIDTH = 240;

    // Get current user roles directly from JWT token for security
    const userRoles = getUserRolesFromToken();

    const isActive = useCallback(
        (path: string) => location.pathname === path || location.pathname.startsWith(path + '/'),
        [location.pathname],
    );

    // Filter navigation items based on user roles - using centralized functions
    const getFilteredNavItems = () => {
        return rolePermissions.getFilteredNavItems(userRoles, NAV_ITEMS);
    };


    const filteredNavItems = getFilteredNavItems();

    const drawerContent = (
        <>
            <Toolbar />
            <List sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {filteredNavItems.map(item => (
                    <ListItemButton
                        key={item.label}
                        onClick={() => {
                            navigate(item.path);
                            if (isMobile) onClose(); // close drawer
                        }}
                        sx={isActive(item.path) ? activeListItemStyles : listItemStyles}
                    >
                        <ListItemIcon
                            sx={{
                                color: isActive(item.path) ? '#0A1D56' : '#FF897E',
                                minWidth: '36px',
                            }}
                        >
                            {item.icon}
                        </ListItemIcon>
                        <ListItemText
                            primary={
                                <Typography fontWeight={500} fontSize='14px'>
                                    {item.label}
                                </Typography>
                            }
                        />
                    </ListItemButton>
                ))}
            </List>
        </>
    );

    return (
        <Drawer
            variant={isMobile ? 'temporary' : 'permanent'}
            anchor='left'
            open={isOpen}
            onClose={onClose}
            ModalProps={{ keepMounted: true }}
            sx={{
                width: DRAWER_WIDTH,
                flexShrink: 0,
                [`& .MuiDrawer-paper`]: {
                    width: DRAWER_WIDTH,
                    boxSizing: 'border-box',
                    paddingTop: '16px',
                    paddingX: '12px',
                    boxShadow: '2px 0 6px rgba(0, 0, 0, 0.08)',
                },
            }}
        >
            {drawerContent}
        </Drawer>
    );
};

export default VerificationSystemSidebar;
